/**
 * Kafel projektu na stronie /realizacje.
 *
 * Dwa warianty w jednym komponencie, rozroznione przez `access`:
 * - `klikalne`  -> caly kafel jest linkiem, stopka mowi "Otworz bez logowania"
 * - `zamkniete` -> kafel to <article>, stopka mowi WPROST dlaczego nie da sie kliknac
 *
 * Kafla zamknietego celowo NIE robimy linkiem donikad. Klikniecie, ktore nic nie robi,
 * kosztuje wiecej zaufania niz uczciwe zdanie "tu sa dane klienta".
 *
 * Kolory wylacznie przez tokeny (bg-surface, text-muted, border-stroke, text-accent-to).
 */
import { Link } from 'react-router-dom';
import { ArrowUpRight, Lock } from 'lucide-react';
import type { Project } from '../../data/realizacje';

/** Wewnetrzne trasy renderujemy przez <Link>, zewnetrzne przez <a target="_blank">. */
function isExternal(href: string) {
  return href.startsWith('http');
}

function TileBody({ project }: { project: Project }) {
  const open = project.access === 'klikalne';

  return (
    <>
      {/*
        Miniatura 16:9. `loading="lazy"` + jawne width/height, zeby nie bylo przesuniecia
        layoutu (CLS). Projekty bez zrzutu dostaja pasek z tokenow zamiast pustej dziury -
        NIE wstawiamy tu wygenerowanego mockupu, bo strona obiecuje realne ekrany.
      */}
      <div className="relative -mx-6 -mt-6 md:-mx-7 md:-mt-7 mb-6 aspect-video overflow-hidden rounded-t-2xl bg-bg border-b border-stroke">
        {project.thumb ? (
          <img
            src={project.thumb}
            alt={project.thumbAlt ?? ''}
            width={1200}
            height={675}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-surface to-bg">
            <span className="text-[11px] uppercase tracking-[0.25em] text-muted px-6 text-center">
              Podgląd niedostępny
            </span>
          </div>
        )}
        <span
          // bg-text-primary + text-bg, NIE bg-accent-to + text-white. Oba tokeny
          // odwracaja sie razem przy zmianie motywu, wiec kontrast trzyma sie w jasnym
          // i w ciemnym. Biel na akcencie daje ~3,3:1 przy progu 4,5 dla takiego drobnego druku.
          className={`absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full ${
            open
              ? 'bg-text-primary text-bg'
              : 'bg-bg/90 text-muted border border-stroke backdrop-blur-sm'
          }`}
        >
          {open ? 'Klikalne' : 'Za logowaniem'}
        </span>

        {/*
          Grafika wygenerowana MUSI byc oznaczona. Strona obiecuje realne ekrany;
          niepodpisana ilustracja obok zdania "to dziala na produkcji" psuje dokladnie
          to zaufanie, ktore ta strona ma budowac.
        */}
        {project.thumbKind === 'ilustracja' && (
          <span className="absolute bottom-3 right-3 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-bg/90 text-muted border border-stroke backdrop-blur-sm">
            Ilustracja, nie zrzut
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <span className="text-[11px] uppercase tracking-[0.2em] text-muted">{project.category}</span>
      </div>

      <h3 className="text-xl md:text-2xl font-light text-text-primary mb-3 tracking-tight">
        {project.title}
      </h3>

      <p className="text-sm text-muted leading-relaxed mb-5">{project.description}</p>

      {project.metric && (
        <div className="mb-5 pl-4 border-l-2 border-accent-from">
          <p className="text-sm font-medium text-text-primary">{project.metric}</p>
          {project.metricSource && (
            <p className="text-xs text-muted mt-1 leading-relaxed">{project.metricSource}</p>
          )}
        </div>
      )}

      <p className="text-xs text-muted mb-5">{project.stack}</p>

      <div className="mt-auto pt-4 border-t border-stroke">
        {open ? (
          // text-text-primary, NIE text-accent-to. Zmierzone Lighthouse'em: accent na
          // tle surface daje kontrast 3,09 przy progu 4,5 dla tekstu 14px. Sygnal
          // klikalnosci niosa strzalka i podswietlenie ramki na hover, nie kolor tekstu.
          <span className="inline-flex items-center gap-2 text-sm text-text-primary font-medium">
            Otwórz bez logowania
            <ArrowUpRight className="w-4 h-4 text-accent-to transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        ) : (
          <span className="inline-flex items-start gap-2 text-xs text-muted leading-relaxed">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
            {project.closedReason}
          </span>
        )}
      </div>
    </>
  );
}

const SHELL =
  'group flex flex-col h-full bg-surface border border-stroke rounded-2xl p-6 md:p-7 transition-all duration-200';
const OPEN_SHELL = `${SHELL} hover:-translate-y-[3px] hover:border-accent-from`;

export default function ProjectTile({ project }: { project: Project }) {
  if (project.access !== 'klikalne' || !project.href) {
    return (
      <article className={SHELL}>
        <TileBody project={project} />
      </article>
    );
  }

  if (isExternal(project.href)) {
    return (
      // Bez aria-label. Nazwa dostepna pochodzi z tresci kafla; recznie wpisany
      // aria-label ja NADPISUJE i wtedy widoczny tekst "Otworz bez logowania" nie
      // zawiera sie w nazwie - Lighthouse zglasza label-content-name-mismatch.
      <a href={project.href} target="_blank" rel="noopener noreferrer" className={OPEN_SHELL}>
        <TileBody project={project} />
      </a>
    );
  }

  return (
    <Link to={project.href} className={OPEN_SHELL}>
      <TileBody project={project} />
    </Link>
  );
}
